import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/console/features/card-layout/components/card/card';
import { TEmptyCard } from '@/console/features/card-layout/components/card/card.types';
import { DeviceDrawer } from '@/console/features/devices/components/device-drawer/device-drawer';
import { GatewaysHealthProvider } from '@/console/features/gateways/hooks/use-gateway-health/gateways-health-context';
import { AddIcon } from '@/shared/assets/icons/add-icon';
import { DevicesIcon } from '@/shared/assets/icons/devices-icon';
import { ShowIcon } from '@/shared/assets/icons/show-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useBackgroundRefetch } from '@/shared/hooks/use-background-refetch/use-background-refetch';
import {
  devicesApi,
  useReadAllDevicesQuery,
} from '@/shared/redux/api/assets/devices/devices-api';
import { DeviceFragment } from '@/shared/types/types.generated';

import { DeviceCardRow } from './components/device-card-row/device-card-row';

export const RecentDevicesCard = () => {
  const { t } = useTranslation();
  const prefetchDevices = devicesApi.usePrefetch('ReadAllDevices');
  const { data, isLoading, isFetching } = useReadAllDevicesQuery({
    pagination: {
      page: 1,
      pageSize: 15,
    },
  });
  const visibleIsFetching = useBackgroundRefetch(isFetching);
  const showLoader = isLoading || visibleIsFetching;
  const devices = data?.readAllDevices?.items;
  const navigate = useNavigate();

  const renderPopoverContent = () => {
    return (
      <>
        <MenuItem onClick={() => navigate('/devices')} icon={<ShowIcon />}>
          {t('card_layout:view_all_devices')}
        </MenuItem>
        <DeviceDrawer component="MenuItem" icon={<AddIcon />} disableTabs />
      </>
    );
  };

  const getEmptyCardProps = (): TEmptyCard => {
    return {
      action: (
        <DeviceDrawer component="Button" icon={<AddIcon />} disableTabs />
      ),
      icon: <DevicesIcon />,
      text: t('card_layout:devices.empty_message'),
    };
  };

  return (
    <GatewaysHealthProvider>
      <Card<DeviceFragment>
        onMouseEnter={() => prefetchDevices()}
        title={t('card_layout:devices.title')}
        emptyCard={getEmptyCardProps()}
        data={devices}
        row={(device) => (
          <DeviceCardRow key={device.deviceId} device={device} />
        )}
        showLoader={showLoader}
        popoverContent={renderPopoverContent()}
      />
    </GatewaysHealthProvider>
  );
};
